import { useCallback, useMemo, useRef, useState } from 'react';
import type SignatureCanvasType from 'react-signature-canvas';
import {
  apiErrorCode,
  stageFormPackageV2Upload,
  type ApiError,
  type V2UploadKind,
  type V2UploadResult,
} from '../../api/bookingApi';
import { canvasToDataUrl, canvasToPngBlob, trimmedCanvasToPngBlob } from './canvasPng';
import { loadOrCreateV2Session, recordStagedUpload } from './v2Session';
import {
  MAX_UPLOADS_PER_SUBMISSION,
  SIGNATURE_MAX_BYTES,
  type SlotKey,
  type StagedUpload,
} from './types';

// Staged-upload bookkeeping for one v2 package: which slot holds which upload id,
// how many uploads this instance has consumed (the server caps staged rows at 20
// per instance, and a redraw stages a new one), and the closed error vocabulary
// the renderer turns into inline copy.

export type V2UploadErrorKind =
  | 'expired'       // 401 — the link is no longer valid
  | 'unavailable'   // 404 / 410 — package gone or already submitted
  | 'rate_limited'  // 429
  | 'too_large'     // 413 or the server's size code
  | 'cap_reached'   // client-side or server-side upload limit
  | 'rejected'      // any other 4xx
  | 'network';      // no response

export class V2UploadError extends Error {
  kind: V2UploadErrorKind;
  status?: number;

  constructor(kind: V2UploadErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'V2UploadError';
    this.kind = kind;
    this.status = status;
    Object.setPrototypeOf(this, V2UploadError.prototype);
  }
}

const MESSAGES: Record<V2UploadErrorKind, string> = {
  expired: 'This link is no longer valid. Please ask the office for a new one.',
  unavailable: 'These forms are no longer available. Please ask the office for a new link.',
  rate_limited: 'Please wait a moment and try again.',
  too_large: 'That image is too large. Please try a smaller one.',
  cap_reached: "You've reached the upload limit for this link. Your earlier upload is kept.",
  rejected: "That couldn't be saved. Please try again.",
  network: "We couldn't save that right now. Please check your connection and try again.",
};

export function classifyUploadError(err: unknown): V2UploadError {
  if (err instanceof V2UploadError) return err;
  const status = (err as ApiError | undefined)?.status;
  const code = apiErrorCode(err);
  if (status === 401) return new V2UploadError('expired', MESSAGES.expired, status);
  if (status === 404 || status === 410) return new V2UploadError('unavailable', MESSAGES.unavailable, status);
  if (status === 429) return new V2UploadError('rate_limited', MESSAGES.rate_limited, status);
  if (status === 413 || code === 'upload_too_large') return new V2UploadError('too_large', MESSAGES.too_large, status);
  if (code === 'upload_limit_exceeded') return new V2UploadError('cap_reached', MESSAGES.cap_reached, status);
  if (status === 503) return new V2UploadError('network', MESSAGES.network, status);
  if (typeof status === 'number') return new V2UploadError('rejected', MESSAGES.rejected, status);
  return new V2UploadError('network', MESSAGES.network);
}

export function uploadErrorMessage(err: unknown): string {
  return classifyUploadError(err).message;
}

export interface UseV2Uploads {
  uploads: Record<SlotKey, StagedUpload>;
  /** Synchronous mirror of `uploads` for use inside async handlers and the payload builder. */
  uploadsRef: React.MutableRefObject<Record<SlotKey, StagedUpload>>;
  idempotencyKey: string;
  stagedCount: number;
  stageSignature: (slot: SlotKey, canvas: SignatureCanvasType | null | undefined) => Promise<StagedUpload>;
  stageFile: (slot: SlotKey, kind: V2UploadKind, file: File) => Promise<V2UploadResult>;
  hydrate: (saved: Record<SlotKey, StagedUpload>) => void;
  dropSlot: (slot: SlotKey) => void;
  clearAll: () => void;
}

export function useV2Uploads(args: { clientId: string; token: string; packageInstanceId: string }): UseV2Uploads {
  const { clientId, token, packageInstanceId } = args;
  const session = useMemo(() => loadOrCreateV2Session(packageInstanceId), [packageInstanceId]);
  const [uploads, setUploads] = useState<Record<SlotKey, StagedUpload>>({});
  const uploadsRef = useRef<Record<SlotKey, StagedUpload>>({});
  const stagedCountRef = useRef<number>(session.staged_upload_ids.length);
  const [stagedCount, setStagedCount] = useState<number>(session.staged_upload_ids.length);

  const commit = useCallback((slot: SlotKey, staged: StagedUpload) => {
    const record = recordStagedUpload(packageInstanceId, staged.uploadId);
    stagedCountRef.current = record.staged_upload_ids.length;
    setStagedCount(record.staged_upload_ids.length);
    uploadsRef.current = { ...uploadsRef.current, [slot]: staged };
    setUploads(uploadsRef.current);
  }, [packageInstanceId]);

  const assertBudget = useCallback(() => {
    // Replacing a slot still stages a new row server-side, so redraws count too.
    if (stagedCountRef.current >= MAX_UPLOADS_PER_SUBMISSION) {
      throw new V2UploadError('cap_reached', MESSAGES.cap_reached);
    }
  }, []);

  const stageSignature = useCallback(async (slot: SlotKey, canvas: SignatureCanvasType | null | undefined) => {
    assertBudget();
    let blob = await canvasToPngBlob(canvas);
    if (!blob) throw new V2UploadError('rejected', 'Please draw your signature.');
    if (blob.size > SIGNATURE_MAX_BYTES) {
      const trimmed = await trimmedCanvasToPngBlob(canvas);
      if (!trimmed || trimmed.size > SIGNATURE_MAX_BYTES) {
        throw new V2UploadError('too_large', 'Signature image is too large. Please clear and sign again.');
      }
      blob = trimmed;
    }
    let result: V2UploadResult;
    try {
      result = await stageFormPackageV2Upload(clientId, token, 'signature', blob, 'signature.png');
    } catch (err) {
      throw classifyUploadError(err);
    }
    const staged: StagedUpload = {
      uploadId: result.upload_id,
      uploadKind: 'signature',
      byteSize: result.byte_size,
      previewUrl: canvasToDataUrl(canvas) ?? undefined,
    };
    commit(slot, staged);
    return staged;
  }, [assertBudget, clientId, commit, token]);

  const stageFile = useCallback(async (slot: SlotKey, kind: V2UploadKind, file: File) => {
    assertBudget();
    let result: V2UploadResult;
    try {
      result = await stageFormPackageV2Upload(clientId, token, kind, file, file.name);
    } catch (err) {
      throw classifyUploadError(err);
    }
    commit(slot, { uploadId: result.upload_id, uploadKind: kind, byteSize: result.byte_size, fileName: file.name });
    return result;
  }, [assertBudget, clientId, commit, token]);

  // Seed the slot→upload map from a resumed draft. The rows already exist
  // server-side (referenced by id), so this does not re-stage or re-count them;
  // it only makes validation and the review screen see them again. Previews are
  // absent from a draft, so a resumed signature shows blank until redrawn.
  const hydrate = useCallback((saved: Record<SlotKey, StagedUpload>) => {
    uploadsRef.current = { ...saved };
    setUploads(uploadsRef.current);
  }, []);

  const dropSlot = useCallback((slot: SlotKey) => {
    const next = { ...uploadsRef.current };
    delete next[slot];
    uploadsRef.current = next;
    setUploads(next);
  }, []);

  const clearAll = useCallback(() => {
    uploadsRef.current = {};
    setUploads({});
  }, []);

  return {
    uploads,
    uploadsRef,
    idempotencyKey: session.idempotency_key,
    stagedCount,
    stageSignature,
    stageFile,
    hydrate,
    dropSlot,
    clearAll,
  };
}
